import { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route } from 'react-router-dom';
import { AuthProvider } from './contexts/AuthContext';
import { PersonalityProvider } from './contexts/PersonalityContext';
import { AudienceProvider } from './contexts/AudienceContext';
import IframeGuard from './components/IframeGuard';

// ---------------------------------------------------------------------------
// Lesson route manifest
// ---------------------------------------------------------------------------
// The 32 teen lessons and 32 adult lessons live in parallel directories. Rather
// than writing out 64 `lazy(() => import(...))` lines + 64 <Route> elements, we
// generate them from a count.
// ---------------------------------------------------------------------------

const TEEN_LESSON_COUNT  = 32;
const ADULT_LESSON_COUNT = 32;

function pad(n: number): string {
  return n.toString().padStart(2, '0');
}

const teenLessonComponents = Array.from({ length: TEEN_LESSON_COUNT }, (_, i) =>
  lazy(() => import(`./lessons/Lesson${pad(i + 1)}/index.tsx`))
);

const adultLessonComponents = Array.from({ length: ADULT_LESSON_COUNT }, (_, i) =>
  lazy(() => import(`./lessons-adult/Lesson${pad(i + 1)}/index.tsx`))
);

// Shared pages
const PhaseStudyGuide = lazy(() => import('./pages/PhaseStudyGuide'));
const PhaseTest       = lazy(() => import('./pages/PhaseTest'));
const FinalExam       = lazy(() => import('./pages/FinalExam'));

// Loading component
const LoadingSpinner = () => (
  <div style={{
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    fontSize: '1.25rem',
    color: '#4b5563'
  }}>
    Loading lesson...
  </div>
);

// 404 Not Found page
const NotFound = () => (
  <div style={{
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    padding: '2rem',
    textAlign: 'center',
    backgroundColor: '#f9fafb'
  }}>
    <h1 style={{ fontSize: '4rem', color: '#3B82F6', marginBottom: '1rem' }}>404</h1>
    <h2 style={{ fontSize: '1.5rem', color: '#1f2937', marginBottom: '1rem' }}>Page Not Found</h2>
    <p style={{ color: '#6b7280', maxWidth: '400px' }}>
      The page you're looking for doesn't exist. Lessons are accessed through the dashboard.
    </p>
  </div>
);

function App() {
  return (
    <IframeGuard>
      <AuthProvider>
        <PersonalityProvider>
          <BrowserRouter>
            <AudienceProvider>
              <Suspense fallback={<LoadingSpinner />}>
                <Routes>
                  <Route path="/" element={<NotFound />} />

                  {/* Teen lessons */}
                  {teenLessonComponents.map((Component, i) => (
                    <Route
                      key={`teen-${i + 1}`}
                      path={`/financial-literacy/lesson/${i + 1}`}
                      element={<Component />}
                    />
                  ))}
                  <Route path="/financial-literacy/phase/:phaseId/study-guide" element={<PhaseStudyGuide />} />
                  <Route path="/financial-literacy/phase/:phaseId/test"        element={<PhaseTest />} />
                  <Route path="/financial-literacy/final-exam"                 element={<FinalExam />} />

                  {/* Adult lessons */}
                  {adultLessonComponents.map((Component, i) => (
                    <Route
                      key={`adult-${i + 1}`}
                      path={`/adult-financial-literacy/lesson/${i + 1}`}
                      element={<Component />}
                    />
                  ))}
                  <Route path="/adult-financial-literacy/phase/:phaseId/study-guide" element={<PhaseStudyGuide />} />
                  <Route path="/adult-financial-literacy/phase/:phaseId/test"        element={<PhaseTest />} />
                  <Route path="/adult-financial-literacy/final-exam"                 element={<FinalExam />} />

                  <Route path="*" element={<NotFound />} />
                </Routes>
              </Suspense>
            </AudienceProvider>
          </BrowserRouter>
        </PersonalityProvider>
      </AuthProvider>
    </IframeGuard>
  );
}

export default App;
